import { useState } from 'react'
import { AssaySDK } from '@diwata-labs/assay-sdk'
import { useUI } from '@/store/ui'
import { Button, Card } from '@diwata/ironvale'

type CaptureState = 'idle' | 'capturing' | 'success' | 'error'

export function AssayPanel() {
  const { assayUrl, assayApiKey } = useUI()
  const [state, setState] = useState<CaptureState>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [comment, setComment] = useState('')

  const hasKey = assayApiKey.trim() !== ''

  async function capture() {
    if (!hasKey) return
    setState('capturing')
    setErrorMsg('')
    try {
      const sdk = new AssaySDK({ apiKey: assayApiKey, endpoint: assayUrl })
      await sdk.capture({ comment: comment.trim() || undefined })
      setState('success')
      setComment('')
      setTimeout(() => setState('idle'), 3000)
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setState('error')
    }
  }

  return (
    <div>
      <div className="ap-page-header">
        <div>
          <div className="ap-page-title">Assay</div>
          <div className="ap-page-subtitle">Screenshot capture and verification</div>
        </div>
        <a className="iv-button iv-button--ghost iv-button--sm" href={assayUrl} target="_blank" rel="noreferrer">
          Open dashboard ↗
        </a>
      </div>

      <Card>
        {!hasKey ? (
          <p style={{ fontSize: 13, color: 'var(--ae-color-text-muted)', lineHeight: 1.6 }}>
            No Assay API key configured. Add one in{' '}
            <strong style={{ color: 'var(--ae-color-text-primary)' }}>Settings → Assay API Key</strong>.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--ae-color-text-muted)', margin: 0 }}>
              Captures a screenshot of the current browser tab and sends it to Assay at{' '}
              <strong style={{ color: 'var(--ae-color-text-primary)' }}>{assayUrl}</strong>.
            </p>

            <div className="ap-field">
              <label className="ap-label">Comment (optional)</label>
              <input
                className="iv-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What are you verifying?"
                disabled={state === 'capturing'}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button onClick={capture} disabled={state === 'capturing'}>
                {state === 'capturing' ? 'Capturing…' : 'Capture now'}
              </Button>
              {state === 'success' && (
                <span style={{ fontSize: 13, color: 'var(--ae-color-feedback-success)' }}>
                  Captured
                </span>
              )}
              {state === 'error' && (
                <span style={{ fontSize: 13, color: 'var(--ae-color-feedback-error)' }}>
                  {errorMsg}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
